import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth, API, socket } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send, Users, UserPlus, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import GroupConferenceButton from '../components/GroupConferenceButton';

export default function GroupChat() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchGroup();
    fetchMessages();
    fetchMembers();

    // Join group room for real-time messages
    if (socket && groupId) {
      socket.emit('join_room', { room: `group_${groupId}` });
      
      socket.on('group_message', (message) => {
        setMessages(prev => [...prev, message]);
      });
    }

    return () => {
      if (socket && groupId) {
        socket.emit('leave_room', { room: `group_${groupId}` });
        socket.off('group_message');
      }
    };
  }, [groupId, socket]);

  const fetchGroup = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}`);
      setGroup(response.data);
    } catch (error) {
      console.error('Error fetching group:', error);
      toast.error(t('errors.generic'));
      navigate('/groups');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/members`, { withCredentials: true });
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setLoading(true);
    try {
      await axios.post(
        `${API}/groups/${groupId}/messages`,
        { content: newMessage },
        { withCredentials: true }
      );
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (!group) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Group Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/groups')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{group.name}</h1>
            <p className="text-gray-600 text-sm">{group.description}</p>
          </div>
          <div className="flex items-center gap-3">
            <GroupConferenceButton groupId={groupId} groupName={group.name} />
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Users className="h-4 w-4" />
              <span>{group.member_count} {t('groups.members')}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Chat Area */}
          <Card className="lg:col-span-3 p-0 overflow-hidden flex flex-col" style={{ height: '600px' }}>
            {/* Messages */}
            <ScrollArea className="flex-1 p-6">
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex gap-3 ${msg.sender_id === user.id ? 'flex-row-reverse' : ''}`}>
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={msg.sender_picture} />
                      <AvatarFallback className="bg-teal-100 text-teal-600">
                        {msg.sender_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className={`flex-1 ${msg.sender_id === user.id ? 'text-right' : ''}`}>
                      <p className="text-xs text-gray-500 mb-1">{msg.sender_name}</p>
                      <div className={`inline-block p-3 rounded-lg ${
                        msg.sender_id === user.id
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}>
                        <p className="text-sm">{msg.content}</p>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Message Input */}
            <div className="border-t border-gray-200 p-4 bg-white">
              <div className="flex gap-2">
                <Input
                  placeholder={t('groups.typeMessage')}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !loading && handleSendMessage()}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={loading || !newMessage.trim()}
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Members Sidebar */}
          <Card className="p-6" style={{ height: '600px' }}>
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('groups.members')}
            </h3>
            <ScrollArea className="h-[520px]">
              <div className="space-y-3">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={member.picture} />
                      <AvatarFallback className="bg-teal-100 text-teal-600">
                        {member.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{member.name}</p>
                      <p className="text-xs text-gray-500 truncate">@{member.username}</p>
                    </div>
                    {member.id === group.creator_id && (
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 text-xs">
                        {t('groups.creator')}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </Card>
        </div>
      </div>

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('groups.createGroup')}</DialogTitle>
            <DialogDescription>{t('groups.createDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('groups.groupName')}</label>
              <Input
                placeholder={t('groups.namePlaceholder')}
                value={newGroup.name}
                onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                maxLength={50}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('groups.category')}</label>
              <Select value={newGroup.category} onValueChange={(val) => setNewGroup({...newGroup, category: val})}>
                <SelectTrigger>
                  <SelectValue placeholder={t('groups.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('groups.description')}</label>
              <Textarea
                rows={3}
                placeholder={t('groups.descriptionPlaceholder')}
                value={newGroup.description}
                onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreateGroup} disabled={loading} className="bg-teal-500 hover:bg-teal-600">
              {loading ? t('common.processing') : t('groups.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
